import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  GerantRow,
  LogicielCaisseParametreAssociationsService,
} from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import {
  LogicielCaisseEntrepotOption,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';

@Component({
  selector: 'app-logiciel-caisse-gerant-entrepot',
  templateUrl: './logiciel-caisse-gerant-entrepot.component.html',
  styleUrls: ['./logiciel-caisse-gerant-entrepot.component.scss'],
})
export class LogicielCaisseGerantEntrepotComponent implements OnInit {
  loading = false;
  saving = false;
  rows: GerantRow[] = [];
  filteredRows: GerantRow[] = [];
  searchTerm = '';
  editId: number | null = null;
  form: UntypedFormGroup;

  entrepotOptions: LogicielCaisseEntrepotOption[] = [];
  entrepotIdsByGerant = new Map<number, string[]>();
  entrepotLabelsByGerant = new Map<number, string[]>();

  associationRow: GerantRow | null = null;
  associationEntrepotIds: string[] = [];
  associationSaving = false;

  constructor(
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      nom: ['', Validators.required],
      prenom: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [''],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  gerantLabel(row: GerantRow): string {
    return `${row.prenom} ${row.nom}`.trim() || row.email;
  }

  load(): void {
    this.loading = true;
    forkJoin({
          options: this.entrepotService.getEntrepotOptions(),
          gerants: this.associationsService.listGerants(),
          links: this.associationsService.listGerantEntrepotLinks(),
        }).subscribe({
        next: ({ options, gerants, links }) => {
          this.entrepotOptions = options;
          this.rows = gerants;
          this.applySearch();
          const idsMap = new Map<number, string[]>();
          const labelsMap = new Map<number, string[]>();
          for (const link of links) {
            const gid = Number(link.gerant_id);
            const idList = idsMap.get(gid) ?? [];
            idList.push(String(link.entrepot_id));
            idsMap.set(gid, idList);

            const label =
              link.entrepot_libelle?.trim() ||
              link.code_entrepot?.trim() ||
              `Entrepôt ${link.entrepot_id}`;
            const labelList = labelsMap.get(gid) ?? [];
            labelList.push(label);
            labelsMap.set(gid, labelList);
          }
          this.entrepotIdsByGerant = idsMap;
          this.entrepotLabelsByGerant = labelsMap;
          this.loading = false;
          this.refreshFeatherIcons();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.loading = false;
        },
      });
  }

  private loadEntrepotLinks(): void {
    this.associationsService.listGerantEntrepotLinks().subscribe({
      next: (links) => {
        const idsMap = new Map<number, string[]>();
        const labelsMap = new Map<number, string[]>();
        for (const link of links) {
          const gid = Number(link.gerant_id);
          const idList = idsMap.get(gid) ?? [];
          idList.push(String(link.entrepot_id));
          idsMap.set(gid, idList);

          const label =
            link.entrepot_libelle?.trim() ||
            link.code_entrepot?.trim() ||
            `Entrepôt ${link.entrepot_id}`;
          const labelList = labelsMap.get(gid) ?? [];
          labelList.push(label);
          labelsMap.set(gid, labelList);
        }
        this.entrepotIdsByGerant = idsMap;
        this.entrepotLabelsByGerant = labelsMap;
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        this.loading = false;
      },
    });
  }

  entrepotsSummary(row: GerantRow): string {
    const labels = this.entrepotLabelsByGerant.get(row.id);
    if (!labels?.length) {
      return '—';
    }
    return labels.join(', ');
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${this.gerantLabel(row)} ${row.email}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  private setPasswordValidators(required: boolean): void {
    const control = this.form.get('password');
    if (!control) {
      return;
    }
    if (required) {
      control.setValidators([Validators.required, Validators.minLength(6)]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  openCreate(modal: TemplateRef<unknown>): void {
    this.editId = null;
    this.form.reset();
    this.setPasswordValidators(true);
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openEdit(row: GerantRow, modal: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form.patchValue({
      nom: row.nom,
      prenom: row.prenom,
      email: row.email,
      password: '',
    });
    this.setPasswordValidators(false);
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openAssociation(row: GerantRow, modal: TemplateRef<unknown>): void {
    this.associationRow = row;
    this.associationEntrepotIds = [...(this.entrepotIdsByGerant.get(row.id) ?? [])];
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const body = {
      nom: String(this.form.value.nom).trim(),
      prenom: String(this.form.value.prenom).trim(),
      email: String(this.form.value.email).trim(),
    };
    const password = String(this.form.value.password ?? '').trim();

    if (password && password.length < 6) {
      this.toastr.error('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    this.saving = true;

    if (this.editId) {
      const updateBody = { ...body };
      if (password) {
        Object.assign(updateBody, { password });
      }
      this.associationsService.updateGerant(this.editId, updateBody).subscribe({
        next: () => {
          this.saving = false;
          modalRef.close();
          this.toastr.success('Gérant enregistré.');
          this.load();
        },
        error: (err: Error) => {
          this.saving = false;
          this.toastr.error(err.message);
        },
      });
      return;
    }

    if (!password) {
      this.saving = false;
      this.toastr.error('Le mot de passe est requis.');
      return;
    }

    this.associationsService.createGerant({ ...body, password }).subscribe({
      next: () => {
        this.saving = false;
        modalRef.close();
        this.toastr.success('Gérant créé.');
        this.load();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  deleteRow(row: GerantRow): void {
    if (!confirm(`Supprimer le gérant « ${this.gerantLabel(row)} » ?`)) {
      return;
    }
    this.associationsService.deleteGerant(row.id).subscribe({
      next: () => {
        this.toastr.success('Gérant supprimé.');
        this.load();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
      },
    });
  }

  saveAssociation(modalRef: { close: () => void }): void {
    if (!this.associationRow) {
      return;
    }
    this.associationSaving = true;
    const ids = this.associationEntrepotIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.associationsService.setGerantEntrepots(this.associationRow.id, ids).subscribe({
      next: () => {
        this.associationSaving = false;
        modalRef.close();
        this.associationRow = null;
        this.toastr.success('Association enregistrée.', 'Gérant');
        this.loadEntrepotLinks();
      },
      error: (err: Error) => {
        this.associationSaving = false;
        this.toastr.error(err.message);
      },
    });
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
